/**
 * Request signing shared by every party on the integration: our outbound
 * wallet calls, the operator's inbound calls, the mock operator and the tests
 * all run this exact code, so a signature mismatch can only ever mean a wrong
 * secret rather than two subtly different implementations.
 *
 * Uses Web Crypto (available in Node 20+ and browsers) so the package stays
 * free of Node built-ins and can be bundled anywhere.
 */

export const SIGNATURE_HEADER = 'x-signature';
export const TIMESTAMP_HEADER = 'x-timestamp';
export const OPERATOR_HEADER = 'x-operator-id';
export const KEY_ID_HEADER = 'x-key-id';
export const REQUEST_ID_HEADER = 'x-request-id';

/** Requests older or newer than this are rejected to bound replay attacks. */
export const DEFAULT_SKEW_TOLERANCE_MS = 5 * 60 * 1000;

const encoder = new TextEncoder();

/**
 * The signed string binds the timestamp to the exact bytes of the body. It is
 * critical that verification uses the *raw* body, not a re-serialised object:
 * key order and whitespace would otherwise change the digest.
 */
export function signingPayload(timestamp: string, rawBody: string): string {
  return `${timestamp}.${rawBody}`;
}

/**
 * What gets signed for a given request.
 *
 * Wallet calls are all POSTs, so the body carries everything and signing it is
 * sufficient. The operator API also has GETs whose only meaningful input is the
 * query string, so those sign the request target instead - otherwise the
 * signature would be identical for every GET and would authenticate nothing
 * beyond "this caller knows the secret".
 */
export function canonicalPayload(method: string, url: string, rawBody: string): string {
  const verb = method.toUpperCase();
  return verb === 'GET' || verb === 'HEAD' || verb === 'DELETE' ? url : rawBody;
}

async function importKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify'],
  );
}

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function fromHex(hex: string): Uint8Array<ArrayBuffer> | null {
  if (hex.length === 0 || hex.length % 2 !== 0 || !/^[0-9a-f]+$/i.test(hex)) {
    return null;
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

export async function signRequest(
  secret: string,
  timestamp: string,
  rawBody: string,
): Promise<string> {
  const key = await importKey(secret);
  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(signingPayload(timestamp, rawBody)),
  );
  return toHex(signature);
}

export type SignatureFailureReason = 'INVALID_TIMESTAMP' | 'INVALID_SIGNATURE';

export interface VerifySignatureInput {
  secret: string;
  timestamp: string;
  rawBody: string;
  signature: string;
  now?: number;
  toleranceMs?: number;
}

/**
 * Verification is delegated to `crypto.subtle.verify` rather than a string
 * comparison so the check does not leak the correct digest through timing.
 */
export async function verifyRequestSignature(
  input: VerifySignatureInput,
): Promise<{ valid: boolean; reason?: SignatureFailureReason }> {
  const tolerance = input.toleranceMs ?? DEFAULT_SKEW_TOLERANCE_MS;
  const now = input.now ?? Date.now();

  const parsed = Number.isNaN(Number(input.timestamp))
    ? Date.parse(input.timestamp)
    : Number(input.timestamp);

  if (!Number.isFinite(parsed) || Math.abs(now - parsed) > tolerance) {
    return { valid: false, reason: 'INVALID_TIMESTAMP' };
  }

  const signatureBytes = fromHex(input.signature);
  if (!signatureBytes) {
    return { valid: false, reason: 'INVALID_SIGNATURE' };
  }

  const key = await importKey(input.secret);
  const valid = await crypto.subtle.verify(
    'HMAC',
    key,
    signatureBytes,
    encoder.encode(signingPayload(input.timestamp, input.rawBody)),
  );

  return valid ? { valid: true } : { valid: false, reason: 'INVALID_SIGNATURE' };
}

export interface SignedHeadersInput {
  operatorId: string;
  secret: string;
  rawBody: string;
  keyId?: string;
  requestId?: string;
  timestamp?: string;
}

export async function buildSignedHeaders(
  input: SignedHeadersInput,
): Promise<Record<string, string>> {
  const timestamp = input.timestamp ?? String(Date.now());
  const signature = await signRequest(input.secret, timestamp, input.rawBody);

  const headers: Record<string, string> = {
    'content-type': 'application/json',
    [OPERATOR_HEADER]: input.operatorId,
    [TIMESTAMP_HEADER]: timestamp,
    [SIGNATURE_HEADER]: signature,
  };
  if (input.keyId) headers[KEY_ID_HEADER] = input.keyId;
  if (input.requestId) headers[REQUEST_ID_HEADER] = input.requestId;
  return headers;
}
